import React from "react";
import { FormikValues } from "formik";
import debounce from "lodash.debounce";
import { useSelector } from "react-redux";

import { COUNTRY_BY_INSEE_CODE, DEFAULT_DEBOUNCE } from "@config/consts";
import { URSSAF_FORM, URSSAF_FORM_ERRORS } from "@config/testIds/urssaf";

import { FrenchCommune, Territory } from "@typings/globals";

import {
  OptionPanel,
  HorizontalRule,
  FadeInContainer,
  Heading as PanelHeading,
} from "@ui/Atoms";
import { Row, Column } from "@ui/Atoms/Grid";
import {
  DayPicker,
  DAY_PICKER_THEME_VARIANT,
} from "@ui/Molecules/DayPicker/DayPicker";
import { FormikSelectExtended } from "@ui/Molecules";
import { FormikInputWithError } from "@ui/Molecules/FormikInputWithError";

import { useTranslate } from "@hooks/useTranslate";
import { useLocalisation } from "@hooks/useLocalisation";

import {
  StyledColumn,
  Subtitle,
  Heading,
  UrssafDropdownCustomSingleValue,
  UrssafDropdownCustomOption,
  initialValues,
  FIELDS,
} from "../../UrssafRegistrationForm.helpers";

import {
  getCountryOptions,
  getCommuneOptions,
  getGenderOptions,
} from "./PersonalInformation.helpers";

interface Props {
  hasCompletedBooking: boolean;
  values: FormikValues;
  countries: Territory[];
  communes: FrenchCommune[];
  handleChange: (e: React.ChangeEvent<HTMLElement>) => void;
  setFieldTouched: (
    field: FIELDS,
    value: boolean,
    shouldValidate?: boolean,
  ) => void;
  setFieldValue: (field: FIELDS, value: string | Date | null) => void;
  handleFetchCommunes: (input: string) => void;
}

const PersonalInformation = (props: Props): React.ReactElement => {
  const translate = useTranslate("batmaid_pwa_urssaf");
  const translateGeneric = useTranslate("batmaid_pwa_generic");
  const { language } = useLocalisation();

  const handleSelectChange = ({
    name,
    value,
  }: {
    name: string;
    value: string;
  }) => {
    props.setFieldValue(name as FIELDS, value);
  };

  return (
    <OptionPanel>
      <Row>
        <Column lg={10} centered>
          <PanelHeading type={"h6"}>
            {translate("registration_form_title")}
          </PanelHeading>
          {!props.hasCompletedBooking && (
            <Subtitle>{translate("registration_form_subtitle")}</Subtitle>
          )}
          <HorizontalRule xs={["base_x2", "base_x2", null, null]} />

          <Heading>{translate("subsection_personal_info")}</Heading>

          <Row>
            <StyledColumn md={4}>
              <FormikSelectExtended
                isSearchable={false}
                placeholder={translateGeneric("gender")}
                name={FIELDS.GENDER}
                options={getGenderOptions(translateGeneric)}
                value={props.values[FIELDS.GENDER]}
                onChange={handleSelectChange}
                testId={URSSAF_FORM.GENDER}
                errorTestId={URSSAF_FORM_ERRORS.GENDER}
              />
            </StyledColumn>
            <StyledColumn md={8}>
              <FormikInputWithError
                name={FIELDS.SURNAME}
                label={translate("form_last_name")}
                value={props.values[FIELDS.SURNAME]}
                handleChange={props.handleChange}
                testId={URSSAF_FORM.BIRTH_NAME}
                errorTestId={URSSAF_FORM_ERRORS.BIRTH_NAME}
              />
            </StyledColumn>
          </Row>

          <Row>
            <StyledColumn md={6} withOptionalLabel>
              <FormikInputWithError
                name={FIELDS.BIRTH_NAME}
                label={translate("form_birth_name")}
                value={props.values[FIELDS.BIRTH_NAME]}
                handleChange={props.handleChange}
                testId={URSSAF_FORM.LAST_NAME}
              />
            </StyledColumn>
            <StyledColumn md={6}>
              <FormikInputWithError
                name={FIELDS.FIRST_NAME}
                label={translate("form_first_name")}
                value={props.values[FIELDS.FIRST_NAME]}
                handleChange={props.handleChange}
                testId={URSSAF_FORM.FIRST_NAME}
                errorTestId={URSSAF_FORM_ERRORS.FIRST_NAME}
              />
            </StyledColumn>
          </Row>

          <Row>
            <StyledColumn md={6}>
              <DayPicker
                inputLabel={translate("form_birth_date")}
                inputName={FIELDS.BIRTH_DATE}
                variant={DAY_PICKER_THEME_VARIANT.CLIENT}
                defaultValue={initialValues[FIELDS.BIRTH_DATE]}
                lang={language}
                dateFormatText={translateGeneric("date_format_short")}
                onDayChange={day => {
                  props.setFieldTouched(FIELDS.BIRTH_DATE, true);
                  props.setFieldValue(FIELDS.BIRTH_DATE, day);
                }}
                testIds={{ datePicker: URSSAF_FORM.DATE_OF_BIRTH }}
                errorTestId={URSSAF_FORM_ERRORS.DATE_OF_BIRTH}
              />
            </StyledColumn>
            <StyledColumn md={6}>
              <FormikSelectExtended
                placeholder={translate("form_birth_country")}
                name={FIELDS.BIRTH_PLACE_COUNTRY_CODE}
                options={getCountryOptions(props.countries)}
                value={props.values[FIELDS.BIRTH_PLACE_COUNTRY_CODE]}
                onChange={handleSelectChange}
                testId={URSSAF_FORM.COUNTRY_OF_BIRTH}
                errorTestId={URSSAF_FORM_ERRORS.COUNTRY_OF_BIRTH}
              />
            </StyledColumn>
          </Row>

          {props.values[FIELDS.BIRTH_PLACE_COUNTRY_CODE] ===
            COUNTRY_BY_INSEE_CODE.FRANCE && (
            <FadeInContainer>
              <Row>
                <StyledColumn md={6}>
                  <FormikSelectExtended
                    placeholder={translate("form_birth_commune")}
                    name={FIELDS.BIRTH_PLACE_COMMUNE}
                    options={getCommuneOptions(props.communes)}
                    value={props.values[FIELDS.BIRTH_PLACE_COMMUNE]}
                    customSingleValue={UrssafDropdownCustomSingleValue}
                    customOption={UrssafDropdownCustomOption}
                    onChange={handleSelectChange}
                    onInputChange={debounce(
                      value => props.handleFetchCommunes(value),
                      DEFAULT_DEBOUNCE,
                    )}
                    testId={URSSAF_FORM.COMMUNE_OF_BIRTH}
                  />
                </StyledColumn>
              </Row>
            </FadeInContainer>
          )}
        </Column>
      </Row>
    </OptionPanel>
  );
};

export { PersonalInformation };
